import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Form } from 'antd';
import TimerangeSelectionForm from 'components/TimerangeSelectionForm';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import { saveInputHistory } from 'api/user-api';
import { loggerService } from 'libs/logger';
import { TRANSLATION_ASSIGNMENT_NAME } from 'api/model/services/aitools';
import { getAiError } from '../get-ai-error';
import ProjectSorter from '../ProjectSorter';
import { CUSTOM_ASSIGNMENT_VALUE } from '../assignment-options';
import PromptSelect from './PromptSelect';
import styles from './style.module.less';
import { handlePrompt } from '../ai-prompt-handlers';
const logger = loggerService.getRoot();
const isEmptyString = (str) => str === undefined || str.trim() === '';
const getProjectIds = (projectConfig, values) => {
    if (projectConfig.projectType === 'multiple') {
        const { projectIds } = values;
        return projectIds && projectIds.length > 0
            ? projectIds
            : projectConfig.projectList.map((p) => p.id);
    }
    return [projectConfig.projectId];
};
const AiForm = ({ form, projectConfig, onAiResultChange, onClose, aiAssignments, preselectedTimeRange, projectDuration, loading = false, preselectedPrompt = null, projectLanguage, trsxLabels, }) => {
    const { session, updateTeamSettings } = useSession();
    const watchedCustomPrompt = Form.useWatch('customPrompt', form);
    const watchedPromptId = Form.useWatch('promptId', form);
    const isSubmitDisabled = useMemo(() => (watchedPromptId === undefined && preselectedPrompt === null)
        || (watchedPromptId === CUSTOM_ASSIGNMENT_VALUE && isEmptyString(watchedCustomPrompt)), [watchedPromptId, watchedCustomPrompt, preselectedPrompt]);
    const handleSubmit = async (values) => {
        updateTeamSettings({
            aiPromptId: values.promptId,
        });
        if (values.customPrompt !== undefined) {
            await saveInputHistory(session.connection, 'customPrompt', values.customPrompt);
        }
        onAiResultChange({ status: 'loading' });
        const ids = getProjectIds(projectConfig, values);
        // NOTE: version selection is only available for a single project;
        // trsxLabel[i] maps to projectId[i]
        const trsxLabel = projectConfig.projectType === 'single' && values.trsxLabel !== undefined
            ? [values.trsxLabel]
            : undefined;
        const translateAssignment = aiAssignments.find((a) => a.name === TRANSLATION_ASSIGNMENT_NAME);
        const selectedAssignment = aiAssignments.find((a) => a.value === values.promptId);
        const isTranslate = preselectedPrompt === 'Translation'
            || (selectedAssignment === null || selectedAssignment === void 0 ? void 0 : selectedAssignment.name) === TRANSLATION_ASSIGNMENT_NAME;
        const effectivePrompt = isTranslate ? 'Translation' : preselectedPrompt;
        const createNewProject = isTranslate
            ? (projectConfig.projectType === 'multiple' ? true : values.createNewProject)
            : undefined;
        const promptResult = await handlePrompt(effectivePrompt, {
            connection: session.connection,
            projectIds: ids,
            language: values.language,
            begin: values.begin,
            end: values.end,
            customPrompt: values.customPrompt,
            isCustom: values.promptId === CUSTOM_ASSIGNMENT_VALUE,
            assignment: selectedAssignment === undefined
                ? undefined
                : { name: selectedAssignment.name, scopeKind: selectedAssignment.scopeKind },
            translateAssignment: translateAssignment === undefined
                ? undefined
                : { name: translateAssignment.name, scopeKind: translateAssignment.scopeKind },
            result: selectedAssignment === null || selectedAssignment === void 0 ? void 0 : selectedAssignment.result,
            createNewProject,
            trsxLabel,
            logger,
        });
        if (promptResult === null) {
            onAiResultChange({ status: 'error', message: txt('invalidLanguage') });
            return;
        }
        if (promptResult.isSuccess()) {
            const shouldCloseAfterSuccess = isTranslate
                ? createNewProject === false
                : (selectedAssignment === null || selectedAssignment === void 0 ? void 0 : selectedAssignment.result) === 'StreamedProject';
            if (shouldCloseAfterSuccess) {
                onAiResultChange({ status: 'empty' });
                onClose();
                return;
            }
            const resultData = promptResult.get();
            onAiResultChange({
                status: 'success',
                result: 'text' in resultData
                    ? { text: resultData.text }
                    : { location: resultData.location },
            });
        }
        else if (promptResult.isFail()) {
            onAiResultChange({ status: 'error', message: getAiError(promptResult.err()) });
        }
    };
    return (_jsxs(Form, { onFinish: handleSubmit, form: form, initialValues: Object.assign(Object.assign({}, (projectConfig.projectType === 'multiple' && {
            projectIds: projectConfig.projectList.map((p) => p.id),
        })), { createNewProject: true }), children: [_jsxs("div", { children: [_jsx(PromptSelect, { aiAssignments: aiAssignments, promptId: watchedPromptId, loading: loading, preselectedPrompt: preselectedPrompt, projectLanguage: projectLanguage, form: form, trsxLabels: trsxLabels, isMultipleProjects: projectConfig.projectType === 'multiple' }), projectConfig.projectType === 'multiple' && (_jsx(ProjectSorter, { form: form, projectList: projectConfig.projectList })), projectConfig.projectType === 'single'
                        && projectDuration !== undefined && (_jsx(TimerangeSelectionForm, { form: form, title: txt('projectRange'), preselectedRange: preselectedTimeRange, projectDuration: projectDuration, hideSubmitButton: true }))] }), _jsx("div", { className: styles.submit, children: _jsx("div", { className: styles.submitButton, children: _jsx(Button, { type: "primary", htmlType: "submit", disabled: isSubmitDisabled, loading: loading, children: txt('generate') }) }) })] }));
};
export default AiForm;
